import type { TextChain } from '@/api/types/stowed-element'
import { isLinkedTextFrame } from '@/lib/groups/linked-text-group-policy'
import type { CutCandidate } from './types/cut-candidate'
import type { CutPartition } from './types/cut-partition'

/**
 * Shown after a cut that removed some linked frames but left others behind. Deliberately neutral
 * copy, not an error: the cut *did* happen, and the toast only explains why the selection did not
 * empty out completely (WMPCM-8663 TC-01/04/05).
 */
export function partialCutSkippedMessage(skipped: number): string {
  return skipped === 1
    ? '1 linked frame was not cut because it is locked or frozen.'
    : `${skipped} linked frames were not cut because they are locked or frozen.`
}

/** Auto-dismiss for {@link partialCutSkippedMessage}. */
export const PARTIAL_CUT_TOAST_DURATION_MS = 3000

/**
 * Whether cut must leave this object alone. Unlike copy — see `lockedObjectBlocksCopy` in
 * `clipboard-paste.ts`, where a locked linked frame stays copyable — cut is destructive, so both
 * flags protect unconditionally. `frozen` counts on its own, independent of `locked` (TC-04), and a
 * frame carrying both is protected once, not twice (TC-05).
 */
export function isCutProtected(obj: CutCandidate): boolean {
  return obj.locked === true || obj.frozen === true
}

/**
 * Whether this object is a live member of a text link chain. Membership is keyed by NSD `uid`, not
 * `id`, and covers Text-tool frames *and* shapes carrying `shapeTextParagraphs` (WMPCM-7988) — the
 * same structural test `lockedObjectBlocksCopy` uses. An object with no usable uid has never been
 * linked.
 *
 * Delegates to `isLinkedTextFrame` rather than re-deriving the test from `chainContaining`. This
 * wrapper survives only to carry the cut-specific rationale above and to keep `CutCandidate` as the
 * parameter type callers here already use; the rule itself has one home
 * (`lib/groups/linked-text-group-policy.ts`), which is what WMPCM-8969 asked for.
 */
export function isLinkedChainMember(obj: CutCandidate, chains: TextChain[] | undefined): boolean {
  return isLinkedTextFrame(obj, chains)
}

/**
 * Splits a cut selection into what may actually be removed and what must survive.
 *
 * Cut used to be all-or-nothing: one locked or frozen object anywhere in the selection aborted the
 * whole gesture, so a user could not cut the frames they *were* allowed to cut (WMPCM-8663). Partial
 * cut is scoped to **linked text frames only** — a protected chain member is skipped so the rest of
 * its chain can still go, while a protected object that was never linked keeps the historical abort.
 * That keeps plain shape/text cut behavior (WMPCM-7081, WMPCM-8102) byte-identical.
 *
 * Returning the surviving-member *count* rather than the members themselves is deliberate: the only
 * caller needs it to phrase one toast, and handing back two more arrays would invite a second,
 * divergent notion of "what was skipped" alongside the one the delete gesture already announces.
 */
export function partitionSelectionForCut<S extends CutCandidate, T extends CutCandidate>(params: {
  shapes: readonly S[]
  textBoxes: readonly T[]
  chains: TextChain[] | undefined
}): CutPartition<S, T> {
  const { shapes, textBoxes, chains } = params

  if (shapes.length === 0 && textBoxes.length === 0) return { kind: 'empty' }

  // Locked is tested across the whole selection before frozen, matching the check order the cut gate
  // has always used — so a selection blocked by both still reports the lock message users know.
  const everySelected: CutCandidate[] = [...shapes, ...textBoxes]
  const abortsOn = (reason: 'locked' | 'frozen'): boolean =>
    everySelected.some((obj) => obj[reason] === true && !isLinkedChainMember(obj, chains))

  if (abortsOn('locked')) return { kind: 'blocked', reason: 'locked' }
  if (abortsOn('frozen')) return { kind: 'blocked', reason: 'frozen' }

  // Every protected object still standing here is a chain member, so it is skipped rather than fatal.
  const cuttableShapes = shapes.filter((shape) => !isCutProtected(shape))
  const cuttableText = textBoxes.filter((box) => !isCutProtected(box))
  const skipped = shapes.length - cuttableShapes.length + (textBoxes.length - cuttableText.length)

  if (cuttableShapes.length === 0 && cuttableText.length === 0) {
    // Whole chain protected — a no-op, not a partial cut (TC-03).
    return {
      kind: 'blocked',
      reason: everySelected.some((obj) => obj.locked === true) ? 'locked' : 'frozen',
    }
  }

  return { kind: 'proceed', cuttableShapes, cuttableText, skipped }
}
